import { Box, Paper, Text, Tooltip } from "@mantine/core"
import dayjs from "dayjs"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SegmentedTabs } from "../../../components/ui/segmented-tabs"
import { IByPie, IDashboard } from "../../../models/Dashboard"
import { currencyFormat } from "../../../utils"
import { formatCurrencyShort } from "../utils"
import { CardHeader } from "./CardHeader"
import cardClasses from "../real.module.css"
import classes from "./partner-breakdown.module.css"

// «Харилцагчаар»: the selected month's sales split by customer category or by
// customer, as ranked bars (a donut said nothing with one slice and was hard
// to compare with several). The period is the dashboard's month picker — the
// card sends pieFilter=MONTH, which the backend reads as the month of `date`.
// byPiePartner / byPieGroup come from a newer backend; without them the card
// shows byPie only and hides the «Ангилал | Харилцагч» switch.

type Group = "CATEGORY" | "PARTNER"

interface Props {
  byPie: IDashboard["byPie"] | undefined
  byPiePartner: IDashboard["byPiePartner"]
  byPieGroup: IDashboard["byPieGroup"]
  isBuyer: boolean
  date: string | null
}

// Backend colours of the roll-up rows (utils/dashboard-stats PIE_OTHERS_COLOR,
// PIE_UNCLASSIFIED_COLOR): drawn in gray so the named rows lead.
const MUTED_COLORS = new Set(["#e2362f", "#adb5bd"])

const short = (amount: number) => {
  const { num, unit } = formatCurrencyShort(amount)
  return `${num} ${unit}`
}

export function PartnerBreakdownCard({ byPie, byPiePartner, byPieGroup, isBuyer, date }: Props) {
  const { t } = useTranslation(["dashboard", "common"])
  const canSwitch = byPieGroup === "CATEGORY" && Array.isArray(byPiePartner)
  const [group, setGroup] = useState<Group>("CATEGORY")
  const active: Group = canSwitch ? group : "CATEGORY"

  const rows = useMemo(() => {
    const source = (active === "PARTNER" ? byPiePartner : byPie) ?? []
    // An older backend still sends categories with no orders.
    return source.filter((r) => (r?.ordersCount || 0) > 0 || (r?.amount || 0) > 0)
  }, [active, byPie, byPiePartner])

  const total = rows.reduce((sum, r) => sum + (r?.amount || 0), 0)
  const totalCount = rows.reduce((sum, r) => sum + (r?.ordersCount || 0), 0)
  const month = date ? dayjs(date).format(t("dashboard:dateFormat")) : ""

  return (
    <Paper className={cardClasses.card}>
      <CardHeader
        title={t(isBuyer ? "dashboard:widgets.supplierBreakdown" : "dashboard:widgets.partnerBreakdown")}
        helpKey="order.dashboard.section.partner-breakdown"
        subtitle={t("dashboard:breakdown.subtitle", { month })}
        right={
          canSwitch ? (
            <SegmentedTabs
              value={group}
              onChange={(v) => setGroup(v as Group)}
              data={[
                { value: "CATEGORY", label: t("dashboard:breakdown.byCategory") },
                { value: "PARTNER", label: t("dashboard:breakdown.byPartner") },
              ]}
              aria-label={t("dashboard:breakdown.groupLabel")}
            />
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        <Text className={classes.empty}>{t("dashboard:breakdown.empty")}</Text>
      ) : (
        <>
          <Box className={classes.list}>
            {rows.map((r: IByPie, i) => {
              const amount = r?.amount || 0
              const share = total > 0 ? (amount / total) * 100 : 0
              const pct = `${Math.round(share)}%`
              const orders = t("dashboard:chart.orders", { count: r?.ordersCount || 0 })
              return (
                <Tooltip
                  key={`${r?.name}-${i}`}
                  label={`${r?.name || "-"} · ${currencyFormat(amount)} · ${orders} · ${pct}`}
                  openDelay={150}
                  withinPortal
                >
                  <Box className={classes.row}>
                    <Box className={classes.who}>
                      <Text className={classes.name}>{r?.name || "-"}</Text>
                      <Text className={classes.meta}>{orders}</Text>
                    </Box>
                    <Box className={classes.track}>
                      <Box
                        className={classes.fill}
                        data-muted={MUTED_COLORS.has(String(r?.color).toLowerCase()) || undefined}
                        style={{ width: `${share}%` }}
                      />
                    </Box>
                    <Box className={classes.value}>
                      <Text className={classes.amount}>{short(amount)}</Text>
                      <Text className={classes.meta}>{pct}</Text>
                    </Box>
                  </Box>
                </Tooltip>
              )
            })}
          </Box>
          <Box className={classes.total}>
            <Text className={classes.name}>{t("dashboard:total")}</Text>
            <Text className={classes.amount}>
              {short(total)}
              <Text span className={classes.meta}> · {t("dashboard:chart.orders", { count: totalCount })}</Text>
            </Text>
          </Box>
        </>
      )}
    </Paper>
  )
}
